import { DIAGNOSTICS_API } from "admin/types/constants/diagnostics";
import type { DiagnosticsSearchResponse } from "admin/types/interfaces/diagnostics";
import { useFetcher } from "~/hooks/useFetcher";

interface UseDiagnosticsParams {
  limit?: number;
  offset?: number;
  sortKey?: string;
  sortOrder?: "asc" | "desc";
  severity?: string;
  tier?: string;
  primaryFailureAgent?: string;
  triageStatus?: string;
  /** Exact conversation UUID match (the backend does not support partial search). */
  conversationId?: string;
  /** Inclusive lower bound on created_at, as a YYYY-MM-DD date. */
  dateFrom?: string;
  /** Inclusive upper bound on created_at, as a YYYY-MM-DD date. */
  dateTo?: string;
}

function buildUrl(params: UseDiagnosticsParams): string {
  const qs = new URLSearchParams();

  if (params.limit !== undefined) qs.set("limit", params.limit.toString());
  if (params.offset !== undefined) qs.set("offset", params.offset.toString());
  if (params.sortKey) qs.set("sort_by", params.sortKey);
  if (params.sortOrder) qs.set("sort_order", params.sortOrder);
  if (params.severity) qs.set("severity", params.severity);
  if (params.tier) qs.set("tier", params.tier);
  if (params.primaryFailureAgent) qs.set("primary_failure_agent", params.primaryFailureAgent);
  if (params.triageStatus) qs.set("triage_status", params.triageStatus);
  if (params.conversationId) qs.set("conversation_id", params.conversationId);
  if (params.dateFrom) qs.set("date_from", params.dateFrom);
  if (params.dateTo) qs.set("date_to", params.dateTo);

  const queryString = qs.toString();
  return `${DIAGNOSTICS_API}/${queryString ? `?${queryString}` : ""}`;
}

/**
 * Hook for searching diagnostic records with pagination and filtering.
 * SWR re-fetches automatically when the URL (derived from params) changes.
 */
const useDiagnostics = ({
  limit = 20,
  offset = 0,
  sortKey = "created_at",
  sortOrder = "desc",
  severity,
  tier,
  primaryFailureAgent,
  triageStatus,
  conversationId,
  dateFrom,
  dateTo,
}: UseDiagnosticsParams = {}) => {
  const url = buildUrl({
    limit,
    offset,
    sortKey,
    sortOrder,
    severity,
    tier,
    primaryFailureAgent,
    triageStatus,
    conversationId,
    dateFrom,
    dateTo,
  });

  const { data, error, isLoading } = useFetcher<DiagnosticsSearchResponse>(url, {
    shouldCamelize: true,
    keepPreviousData: true,
  });

  return {
    items: data?.items ?? [],
    total: data?.total,
    limit: data?.limit,
    offset: data?.offset,
    error,
    isLoading,
    /** The resolved SWR key, so callers can revalidate after mutations. */
    key: url,
  };
};

export default useDiagnostics;
